'use client'

import * as React from 'react'
import { Link } from 'react-router-dom'
import { Mail, ArrowLeft } from 'lucide-react'
import { sendPasswordResetEmail } from 'firebase/auth'
import { Button } from 'components/ui/button'
import { Input } from 'components/ui/input'
import { Label } from 'components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from 'components/ui/card'
import { toast } from 'sonner'
import { auth } from '../../../firebase'

export default function ResetPasswordPage() {
  const [isLoading, setIsLoading] = React.useState(false)
  const [sent, setSent] = React.useState(false)
  const [email, setEmail] = React.useState('')

  function getAuthErrorMessage(error) {
    const code = error?.code || ''
    if (code === 'auth/invalid-email') return 'Please enter a valid email address.'
    if (code === 'auth/user-not-found') return 'No account found with that email.'
    if (code === 'auth/too-many-requests') {
      return 'Too many attempts. Please wait a moment and try again.'
    }
    return 'Unable to send reset email right now. Please try again.'
  }

  async function handleSubmit(e) {
    e.preventDefault()
    setIsLoading(true)
    try {
      await sendPasswordResetEmail(auth, email.trim())
      setSent(true)
      toast.success('Password reset email sent. Check your inbox.')
    } catch (error) {
      toast.error(getAuthErrorMessage(error))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center gap-3 lg:hidden">
        <img src="/jooust/logo.jpg" alt="JOOUST Logo" className="size-10 rounded-lg object-cover" />
        <div>
          <h1 className="text-base font-bold text-foreground font-serif">JOOUST PMIS</h1>
          <p className="text-[10px] text-muted-foreground">Project Management & MEL</p>
        </div>
      </div>

      <Card className="border-0 shadow-none lg:border lg:shadow-sm">
        <CardHeader className="px-0 lg:px-6">
          <CardTitle className="text-2xl font-serif font-bold text-foreground">
            Reset password
          </CardTitle>
          <CardDescription className="text-sm text-muted-foreground">
            {sent
              ? 'Check your email for a password reset link'
              : 'Enter your email address and we\'ll send you a reset link'}
          </CardDescription>
        </CardHeader>
        <CardContent className="px-0 lg:px-6">
          {sent ? (
            <div className="flex flex-col gap-4">
              <div className="rounded-lg border border-border bg-muted/50 p-4 text-center">
                <Mail className="mx-auto mb-2 size-8 text-primary" />
                <p className="text-sm text-foreground">
                  If an account exists with that email, a reset link has been sent.
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Check your spam folder if you don&apos;t see it within a few minutes.
                </p>
              </div>
              <Button asChild variant="outline" className="h-10">
                <Link to="/login">
                  <ArrowLeft className="mr-2 size-4" />
                  Back to sign in
                </Link>
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col gap-5">
              <div className="flex flex-col gap-2">
                <Label htmlFor="email" className="text-sm font-medium text-foreground">
                  Email address
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="name@jooust.ac.ke"
                    className="pl-10 h-10"
                    required
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="h-10 w-full bg-primary text-primary-foreground hover:bg-accent font-medium"
                disabled={isLoading}
              >
                {isLoading ? 'Sending...' : 'Send reset link'}
              </Button>

              <Button asChild variant="ghost" className="h-10 text-muted-foreground">
                <Link to="/login">
                  <ArrowLeft className="mr-2 size-4" />
                  Back to sign in
                </Link>
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
